import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { chartTooltipProps } from '../charts/chartTooltip';
import type { Projection, Simulation, YearBand } from '../../services/household-retirement-service';
import { compactMoney, money } from './format';

export type BandSeries = 'net_worth' | 'investable';

/** Chart rows for one series and dollar basis. Exported for tests. */
export function bandRows(sim: Simulation | null, projection: Projection | null, series: BandSeries, real: boolean) {
  if (sim) {
    const bands: YearBand[] = series === 'net_worth'
      ? (real ? sim.net_worth_real : sim.net_worth)
      : (real ? sim.investable_real : sim.investable);
    return bands.map((b) => ({
      year: b.year, outer: [b.p10, b.p90], inner: [b.p25, b.p75], median: b.p50, deterministic: b.deterministic,
    }));
  }
  // Before the Monte Carlo arrives: the deterministic line alone.
  return (projection?.years ?? []).map((y) => ({
    year: y.year,
    deterministic: series === 'net_worth' ? (real ? y.net_worth_real : y.net_worth) : (real ? y.investable_real : y.investable),
  }));
}

/**
 * The household's net worth (or investable assets) over time: the Monte
 * Carlo p10–p90 and p25–p75 bands, its median, and the deterministic
 * "everything goes well" line, with each retirement marked.
 */
export const NetWorthBandChart: React.FC<{
  simulation: Simulation | null;
  projection: Projection | null;
  loading?: boolean;
  currency?: string;
}> = ({ simulation, projection, loading, currency = 'CAD' }) => {
  const theme = useTheme();
  const [series, setSeries] = useState<BandSeries>('net_worth');
  const [real, setReal] = useState(true);
  const rows = useMemo(() => bandRows(simulation, projection, series, real), [simulation, projection, series, real]);
  const retirements = (projection?.summary.at_retirement ?? []).map((p) => ({
    year: Number(p.date.slice(0, 4)) + (Number(p.date.slice(5, 7)) - 1) / 12, name: p.name,
  }));

  return (
    <Card data-testid="band-chart">
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {series === 'net_worth' ? 'Net worth' : 'Investable assets'} — Monte Carlo bands and the “everything goes well” line
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {loading && <CircularProgress size={16} />}
            <ToggleButtonGroup size="small" exclusive value={series} onChange={(_, v) => v && setSeries(v)}>
              <ToggleButton value="net_worth">Net worth</ToggleButton>
              <ToggleButton value="investable">Investable</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={real ? 'real' : 'nominal'} onChange={(_, v) => v && setReal(v === 'real')}>
              <ToggleButton value="real">Today's $</ToggleButton>
              <ToggleButton value="nominal">Future $</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
        <Box sx={{ height: 360 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="year" type="number" domain={['dataMin', 'dataMax']} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v: number) => compactMoney(v, currency)} tick={{ fontSize: 12 }} width={72} />
              <ChartTooltip
                {...chartTooltipProps}
                formatter={(value: any, name: any) =>
                  Array.isArray(value) ? [`${money(value[0], currency)} – ${money(value[1], currency)}`, name] : [money(value, currency), name]}
                labelFormatter={(v: any) => `Year ${v}`}
              />
              {simulation && (
                <>
                  <Area dataKey="outer" name="10th–90th percentile" stroke="none" fill={theme.palette.primary.main}
                    fillOpacity={0.12} isAnimationActive={false} />
                  <Area dataKey="inner" name="25th–75th percentile" stroke="none" fill={theme.palette.primary.main}
                    fillOpacity={0.22} isAnimationActive={false} />
                  <Line dataKey="median" name="Median (Monte Carlo)" dot={false} strokeWidth={2}
                    stroke={theme.palette.primary.main} isAnimationActive={false} />
                </>
              )}
              <Line dataKey="deterministic" name="Everything goes well" dot={false} strokeWidth={2} strokeDasharray="6 3"
                stroke={theme.palette.success.main} isAnimationActive={false} />
              {retirements.map((r, i) => (
                // Alternate sides so two close retirements don't overprint.
                <ReferenceLine key={r.name} x={r.year} stroke={theme.palette.text.secondary} strokeDasharray="3 3"
                  label={{ value: `${r.name} retires`, fontSize: 11, position: i % 2 === 0 ? 'insideTopRight' : 'insideTopLeft' }} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Bands: where 80% and 50% of {simulation?.paths ?? ''} simulated futures land. Net worth bands add the deterministic
          home equity, RESP and vehicles less debt to the simulated portfolio.
        </Typography>
      </CardContent>
    </Card>
  );
};
